import {
  CampaignDetailModelState,
  type StaffGetContractDepositsResponse,
} from '@urbanx/agx.campaigns.client';
import {
  AgxNotificationBox,
  AgxSpacer,
  AgxTypography,
  formatCurrency,
  getAustralianStateTimeZone,
  toDateMediumString,
} from '@urbanx/agx.ui-components2';

interface EarlyReleaseSummaryProps {
  campaignState: CampaignDetailModelState;
  depositReleases: NonNullable<StaffGetContractDepositsResponse['depositReleases']>;
}

type DepositRelease = NonNullable<StaffGetContractDepositsResponse['depositReleases']>[number];
type CanonicalDepositRelease = DepositRelease &
  Required<
    Pick<
      DepositRelease,
      'depositReleaseId' | 'sequence' | 'amount' | 'destinationOfTransfer' | 'submittedUtc'
    >
  >;

const isCanonicalDepositRelease = (release: DepositRelease): release is CanonicalDepositRelease =>
  release.depositReleaseId != null &&
  release.sequence != null &&
  release.amount != null &&
  release.destinationOfTransfer != null &&
  release.submittedUtc != null;

const getDestinationLabel = (release: DepositRelease) => {
  if (release.destinationOfTransfer === 'Other') {
    return release.otherRecipient;
  }

  const destination = release.destinationOfTransfer;
  switch (destination) {
    case 'VendorSolicitor':
      return "Vendor's Solicitor";
    case 'Vendor':
      return 'Vendor';
    default:
      return destination;
  }
};

const formatSubmittedUtc = (submittedUtc: string, campaignState: CampaignDetailModelState) => {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: getAustralianStateTimeZone(campaignState),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(new Date(submittedUtc));
  const getPart = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)!.value;
  const date = toDateMediumString(`${getPart('year')}-${getPart('month')}-${getPart('day')}`);
  if (!date) throw new Error(`Invalid early release submission timestamp: ${submittedUtc}`);

  return `${getPart('hour')}:${getPart('minute')} ${getPart('dayPeriod').toUpperCase()} on ${date}`;
};

const EarlyReleaseSummary = ({ campaignState, depositReleases }: EarlyReleaseSummaryProps) => {
  const warningTextColor = 'var(--text-warning)';

  if (!depositReleases.every(isCanonicalDepositRelease)) {
    throw new Error('Canonical deposit release history is incomplete');
  }

  return (
    <AgxSpacer direction="vertical" fill gap="spacing-md">
      {depositReleases.map((release) => (
        <AgxNotificationBox
          key={release.depositReleaseId}
          type="warning"
          heading={`Early release ${release.sequence}`}
          body={
            <ul className="agx-notification-list">
              <li>
                <AgxTypography
                  id={`early-release-${release.sequence}-amount`}
                  type="body_ui"
                  size="medium"
                  color={warningTextColor}
                >
                  Amount released: <strong>{formatCurrency(release.amount, 2)}</strong>
                </AgxTypography>
              </li>
              <li>
                <AgxTypography
                  id={`early-release-${release.sequence}-recipient`}
                  type="body_ui"
                  size="medium"
                  color={warningTextColor}
                >
                  Recipient: <strong>{getDestinationLabel(release)}</strong>
                </AgxTypography>
              </li>
              <li>
                <AgxTypography
                  id={`early-release-${release.sequence}-submitted`}
                  type="body_ui"
                  size="medium"
                  color={warningTextColor}
                >
                  Submitted:{' '}
                  <strong>{formatSubmittedUtc(release.submittedUtc, campaignState)}</strong>
                </AgxTypography>
              </li>
              <li>
                <AgxTypography
                  id={`early-release-${release.sequence}-actioned-by`}
                  type="body_ui"
                  size="medium"
                  color={warningTextColor}
                >
                  Actioned by:{' '}
                  <strong>{release.actionedByDisplayName ?? release.actionedByUserId}</strong>
                </AgxTypography>
              </li>
            </ul>
          }
        />
      ))}
    </AgxSpacer>
  );
};

export default EarlyReleaseSummary;
